import useStory from './useStory';
import { laneMotion } from '../../data/guideDifference';
import { Explainer } from './GuideParts';

// HOW AN INVERTER CONTROLS ITS SPEED (guide 3, rebuilt 20 Oct 2026). Two
// lanes, the same wall socket in each. The non-inverter's switch sends all
// the power or none; the inverter's circuit board changes it, so its
// compressor turns slower. It plays through three moments in one Delhi
// bedroom (a warm evening, a hot afternoon, a top floor on the hottest day),
// worked by the room model (momentCheck in data/roomDay.js), and rests on the
// last. The waves show which way the power changes, not real figures. No Hz,
// kW or volts on screen.
//
// Props: fixed, inverter (from roomACs()), moments (momentCheck() results
// with key and label), watch ({ title, captions }), sources.

const SECONDS = 20;
const PERIOD = 4;

function wavePath(cycles, phase, amp = 10) {
  let d = '';
  for (let i = 0; i <= 60; i += 1) {
    const y = 20 + amp * Math.sin((2 * Math.PI * cycles * i) / 60 - phase);
    d += `${i ? 'L' : 'M'}${((i / 60) * 100).toFixed(2)} ${y.toFixed(2)}`;
  }
  return d;
}
const FLAT = 'M0 20L100 20';

function Wire({ d, tone }) {
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="block h-9 w-full" aria-hidden="true">
      <line x1="0" x2="100" y1="20" y2="20" stroke="#e5e7eb" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <path d={d} fill="none" stroke={tone} strokeWidth="2.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Box({ children, caption }) {
  return (
    <div className="grid justify-items-center gap-1 text-center">
      <span className="grid h-12 w-12 place-items-center rounded-xl border border-gray-200 bg-white">{children}</span>
      <span className="text-[12px] font-bold leading-tight text-gray-800">{caption}</span>
    </div>
  );
}

function Rotor({ angle, tone }) {
  return (
    <svg width="36" height="36" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r="17" fill="#f9fafb" stroke="#d1d5db" />
      <g transform={`rotate(${angle % 360} 20 20)`} fill={tone}>
        <path d="M20 20c-2-8 1-13 4-13s2 6-1 11z" />
        <path d="M20 20c7.5 3.5 9.5 8.5 7.5 10.5s-6-1.5-8-5.5z" />
        <path d="M20 20c-6 5-11.5 4.5-12.5 1.5s5-5 10-5z" />
      </g>
      <circle cx="20" cy="20" r="2.6" fill="#4b5563" />
    </svg>
  );
}

export default function FrequencyExplainer({ fixed, inverter, moments, watch, sources }) {
  const story = useStory(SECONDS * 1000);
  const t = story.t;
  const m = t < 0.6 ? moments[0] : t < 0.75 ? moments[1] : moments[moments.length - 1];
  const time = t * SECONDS;
  const phase = time * 6;

  const lanes = [
    { ac: fixed, name: 'Non-inverter', mid: 'Switch', tone: '#6b7280' },
    { ac: inverter, name: 'Inverter', mid: 'Circuit board', tone: '#2563eb' },
  ].map((L) => {
    const row = m.rows.find((r) => r.ac.model.id === L.ac.model.id);
    const mo = laneMotion(row, L.ac, L.ac.spec);
    const inCycle = time % PERIOD;
    const on = mo.on >= 0.999 || inCycle < mo.on * PERIOD || t >= 1;
    const turned = Math.floor(time / PERIOD) * mo.on * PERIOD + Math.min(inCycle, mo.on * PERIOD);
    return { ...L, mo, on, angle: turned * 540 * mo.speed };
  });

  return (
    <Explainer id="watch-speed" level={3} title={watch.title} captions={watch.captions} story={story} sources={sources}>
      <p className="text-center text-[15px] font-bold text-gray-900">{m.label}</p>
      {lanes.map((L) => (
        <div key={L.name} className="grid gap-2 rounded-xl border border-gray-200 p-3">
          <p className="flex items-center justify-between gap-2 text-[15px] font-bold text-gray-900">
            {L.name}
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[13px] font-bold text-gray-700">{L.on ? (L.mo.speed < 0.97 ? 'Slower' : 'Full speed') : 'Off'}</span>
          </p>
          <div className="grid grid-cols-[48px_minmax(0,1fr)_64px_minmax(0,1fr)_56px] items-start gap-1">
            <Box caption="Socket">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="4" /><circle cx="9" cy="11" r="1.4" fill="#6b7280" /><circle cx="15" cy="11" r="1.4" fill="#6b7280" /></svg>
            </Box>
            <div className="pt-1.5"><Wire d={wavePath(1.5, phase)} tone={L.tone} /></div>
            <Box caption={L.mid}>
              {L.ac.model.inverter ? (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" fill="#dbeafe" /><path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" /></svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 30" aria-hidden="true">
                  <circle cx="4" cy="22" r="2" fill="#6b7280" /><circle cx="20" cy="22" r="2" fill="#6b7280" />
                  <line x1="4" y1="22" x2="19" y2="22" stroke="#6b7280" strokeWidth="2.4" strokeLinecap="round" transform={L.on ? 'rotate(0 12 22)' : 'rotate(-35 12 22)'} />
                </svg>
              )}
            </Box>
            <div className="pt-1.5"><Wire d={L.on ? wavePath(1.5 * L.mo.speed, phase * L.mo.speed) : FLAT} tone={L.tone} /></div>
            <Box caption="Compressor"><Rotor angle={L.angle} tone={L.tone} /></Box>
          </div>
          <p className="text-[14px] text-gray-600">{L.mo.words}</p>
        </div>
      ))}
    </Explainer>
  );
}
